import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Printer, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiBlob, openBlob } from '../lib/api';

/**
 * Etikettendruck.
 *
 * Ausgabe als PDF in exakter Etikettengröße — eine Seite je Gerät, damit
 * der Dymo LabelWriter ohne Skalierung druckt. Der QR-Code führt auf die
 * Detailseite des Geräts.
 */

interface LabelFormat {
  id: string;
  sku: string;
  label: string;
  width_mm: number;
  height_mm: number;
}

export function LabelSheet({
  assetIds,
  onClose,
}: {
  assetIds: number[];
  onClose: () => void;
}) {
  const [format, setFormat] = useState('dymo-11354');
  const [showModel, setShowModel] = useState(true);
  const [showSerial, setShowSerial] = useState(true);
  const [markPrinted, setMarkPrinted] = useState(true);
  const [busy, setBusy] = useState(false);

  const formats = useQuery({
    queryKey: ['label-formats'],
    queryFn: () => api<LabelFormat[]>('/labels/formats'),
    staleTime: Infinity,
  });

  async function print(): Promise<void> {
    setBusy(true);
    try {
      const { blob } = await apiBlob('/labels/print', {
        method: 'POST',
        body: {
          asset_ids: assetIds,
          format,
          show_model: showModel,
          show_serial: showSerial,
          mark_printed: markPrinted,
        },
      });
      openBlob(blob);
      toast.success(`${assetIds.length} ${assetIds.length === 1 ? 'Etikett' : 'Etiketten'} erzeugt`);
      onClose();
    } catch {
      toast.error('Die Etiketten konnten nicht erzeugt werden');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--color-khadi-forest-900)]/45 backdrop-blur-[2px] sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Etiketten drucken"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="animate-sheet-up safe-bottom w-full max-w-md rounded-t-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-bg-elevated)] p-5 shadow-[var(--shadow-lg)] sm:rounded-[var(--radius-xl)]">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-[family-name:var(--font-display)] text-2xl">Etiketten drucken</h2>
          <button type="button" onClick={onClose} className="btn btn-quiet btn-sm" aria-label="Schließen">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <p className="mb-4 text-sm text-[var(--color-fg3)]">
          {assetIds.length} {assetIds.length === 1 ? 'Gerät' : 'Geräte'} · QR-Code führt auf die
          Geräteseite
        </p>

        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="label-format">
              Etikettenformat
            </label>
            <select
              id="label-format"
              className="field"
              value={format}
              onChange={(event) => setFormat(event.target.value)}
            >
              {formats.data?.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.label}
                </option>
              ))}
            </select>
          </div>

          <fieldset className="space-y-3">
            <legend className="label">Aufdruck</legend>
            <Checkbox checked={showModel} onChange={setShowModel} label="Modellbezeichnung" />
            <Checkbox checked={showSerial} onChange={setShowSerial} label="Seriennummer" />
            <Checkbox
              checked={markPrinted}
              onChange={setMarkPrinted}
              label="Als gedruckt vermerken"
            />
          </fieldset>
        </div>

        <button
          type="button"
          onClick={() => void print()}
          disabled={busy}
          className="btn btn-primary btn-lg mt-5 w-full"
        >
          <Printer className="h-4 w-4" aria-hidden />
          {busy ? 'Wird erzeugt …' : 'PDF öffnen'}
        </button>
      </div>
    </div>
  );
}

function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-[var(--color-accent)]"
      />
      {label}
    </label>
  );
}
